/**
 * deliveryClientPush — Subscribe Web Push para o cliente FINAL (anônimo)
 * atrelado a um pedido específico do Delivery.
 *
 * Diferente do `usePushNotifications`, este helper:
 * - Usa endpoints PÚBLICOS de `/api/delivery/public/*` (sem auth)
 * - Vincula a subscription ao `numero_curto` do pedido (não a um user_id)
 *
 * API:
 *   subscribeToOrderPush(numeroCurto): Promise<{ok: boolean, reason?: string}>
 *
 * Browsers/cenários cobertos:
 * - Sem suporte (Push API ausente) → { ok:false, reason:'unsupported' }
 * - Permissão negada → { ok:false, reason:'denied' }
 * - VAPID não configurado no servidor → { ok:false, reason:'server_unconfigured' }
 * - Falha de rede → { ok:false, reason:'network' }
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const isPushSupported = () =>
  typeof window !== "undefined" &&
  "Notification" in window &&
  "serviceWorker" in navigator &&
  "PushManager" in window;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from(rawData, (c) => c.charCodeAt(0));
}

function bufferToBase64(buffer) {
  if (!buffer) return "";
  const bytes = new Uint8Array(buffer);
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  return window.btoa(bin);
}

export async function subscribeToOrderPush(numeroCurto) {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };
  // 1) Permissão
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, reason: "denied" };

  // 2) VAPID key
  let publicKey;
  try {
    const r = await fetch(`${BACKEND_URL}/api/delivery/public/vapid-public-key`);
    if (r.status === 503) return { ok: false, reason: "server_unconfigured" };
    if (!r.ok) return { ok: false, reason: "network" };
    const data = await r.json();
    publicKey = data.public_key;
  } catch {
    return { ok: false, reason: "network" };
  }
  if (!publicKey) return { ok: false, reason: "server_unconfigured" };

  // 3) Subscribe no browser. `serviceWorker.ready` resolve indefinidamente
  // se nenhum SW foi registrado (caso típico de dev/preview onde o SW só
  // é registrado em NODE_ENV=production). Aplicamos um timeout para devolver
  // erro claro em vez de deixar o UI pendurado.
  let subscription;
  try {
    const reg = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("sw_timeout")), 4000)
      ),
    ]);
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  } catch (e) {
    if (e && e.message === "sw_timeout") {
      return { ok: false, reason: "sw_unavailable" };
    }
    return { ok: false, reason: "subscribe_failed" };
  }

  const rawP256 = subscription.getKey("p256dh");
  const rawAuth = subscription.getKey("auth");

  // 4) Persiste no backend atrelado ao pedido
  try {
    const numero = String(numeroCurto || "").replace(/^#/, "");
    const r = await fetch(
      `${BACKEND_URL}/api/delivery/public/pedido/${encodeURIComponent(numero)}/push-subscribe`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: bufferToBase64(rawP256),
            auth: bufferToBase64(rawAuth),
          },
        }),
      },
    );
    if (!r.ok) return { ok: false, reason: "network" };
  } catch {
    return { ok: false, reason: "network" };
  }
  return { ok: true };
}
